import React, { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';

const ISSUE_OPTIONS = [
  { value: 'location',   label: 'Location' },
  { value: 'fees',       label: 'Fees / tuition costs' },
  { value: 'curriculum', label: 'Curriculum' },
  { value: 'closed',     label: 'School is closed or no longer operating' },
  { value: 'other',      label: 'Other' },
];

const MAX_CORRECTION_LEN = 200;

/**
 * Lightweight modal for reporting a data inaccuracy on a school card.
 * Posts to /api/school-feedback. Submissions are queued for admin review only.
 */
const FlagIssueModal = ({ open, onClose, school, reportId, apiUrl }) => {
  const [issueType, setIssueType] = useState('');
  const [correction, setCorrection] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const close = useCallback(() => {
    if (submitting) return;
    onClose?.();
    // defer reset so the exit animation (if any) reads old state
    setTimeout(() => {
      setIssueType('');
      setCorrection('');
      setDone(false);
      setError('');
    }, 150);
  }, [submitting, onClose]);

  // Close on ESC
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  if (!open || !school) return null;

  const schoolId = school.id || school.school_id || '';
  const schoolName = school.full_name || school.common_name || school.school_name || '';

  const submit = async (e) => {
    e.preventDefault();
    if (!issueType) {
      setError("Please tell us what's incorrect.");
      return;
    }
    if (correction.length > MAX_CORRECTION_LEN) {
      setError(`Please keep the correction under ${MAX_CORRECTION_LEN} characters.`);
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(`${apiUrl}/api/school-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          school_id: schoolId,
          school_name: schoolName,
          issue_type: issueType,
          correction,
          report_id: reportId || null,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Submission failed');
      }
      setDone(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
      data-testid="flag-issue-modal"
      onClick={close}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-[#E8DED3] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 py-4 border-b border-[#E8DED3]">
          <div>
            <h3 className="text-lg font-bold text-[#2C2823]">Notice something wrong?</h3>
            <p className="text-sm text-[#6B6560] mt-0.5 truncate max-w-[22rem]">
              {schoolName}
            </p>
          </div>
          <button
            onClick={close}
            aria-label="Close"
            className="text-[#6B6560] hover:text-[#2C2823] p-1 rounded-md hover:bg-[#FFF5EB]"
            data-testid="flag-modal-close-btn"
            disabled={submitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {done ? (
          <div className="px-6 py-8 text-center" data-testid="flag-modal-success">
            <p className="text-[#2C2823] font-semibold mb-1">Thanks — we'll review this.</p>
            <p className="text-sm text-[#6B6560]">
              Parent flags help us keep the data accurate for everyone.
            </p>
            <button
              onClick={close}
              className="mt-6 inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-[#2C2823] text-white text-sm font-semibold hover:bg-[#1A1816]"
              data-testid="flag-modal-done-btn"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-5">
            <label className="block text-sm font-semibold text-[#2C2823] mb-1.5">
              What's incorrect?
            </label>
            <select
              value={issueType}
              onChange={(e) => setIssueType(e.target.value)}
              className="w-full rounded-lg border border-[#E8DED3] bg-white px-3 py-2.5 text-sm text-[#2C2823] focus:outline-none focus:ring-2 focus:ring-[#E85D35]"
              required
              data-testid="flag-issue-type-select"
            >
              <option value="" disabled>Choose one…</option>
              {ISSUE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>

            <label className="block text-sm font-semibold text-[#2C2823] mt-4 mb-1.5">
              Correct information
            </label>
            <textarea
              value={correction}
              onChange={(e) => setCorrection(e.target.value.slice(0, MAX_CORRECTION_LEN))}
              placeholder="Tell us what's wrong or what the correct information is"
              rows={4}
              maxLength={MAX_CORRECTION_LEN}
              className="w-full rounded-lg border border-[#E8DED3] bg-white px-3 py-2.5 text-sm text-[#2C2823] focus:outline-none focus:ring-2 focus:ring-[#E85D35] resize-none"
              data-testid="flag-correction-textarea"
            />
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-xs text-[#9B8F88]">
                {correction.length}/{MAX_CORRECTION_LEN}
              </span>
              {error && (
                <span className="text-xs text-red-600 font-medium" data-testid="flag-modal-error">
                  {error}
                </span>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={close}
                disabled={submitting}
                className="px-4 py-2 rounded-full text-sm font-semibold text-[#2C2823] hover:bg-[#FFF5EB]"
                data-testid="flag-modal-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-[#E85D35] text-white text-sm font-semibold hover:bg-[#C94F2B] disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="flag-modal-submit-btn"
              >
                {submitting ? 'Submitting…' : 'Submit'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default FlagIssueModal;
